import { useEffect, useRef, useState } from "react";
import { Employee, WorkTask } from "../../types/ganttChart";
import {
  addFilter,
  addItemToSearchHistory,
  clearFilters,
  clearHistory,
  SearchItem,
  setHighlightedTasks,
} from "../../store/search";
import searchStyles from "../../styles/search.module.css";
import labels from "../../utils/labels";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import SearchResult from "./SearchResult";
import SearchIndex from "../../service/SearchIndex";
import SearchBar from "./SearchBar";
import SearchItemChip from "./SearchItemChip";

const GanttChartSearchBar = ({
  employees,
  unplannedTasks,
}: {
  employees: Employee[];
  unplannedTasks: WorkTask[];
}) => {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<SearchItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const { appliedFilters, history } = useAppSelector(
    (state) => state.searchSlice,
  );

  const searchIndex = new SearchIndex([
    ...employees.flatMap((employee) => employee.tasks.workTasks),
    ...unplannedTasks,
  ]);

  const fetchSuggestions = () => {
    if (input.length > 0) {
      const results = searchIndex.suggest(input, appliedFilters);
      setSuggestions(results);
      setIsOpen(results.length > 0);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
    setActiveIndex(-1);
  }, [input]);

  const selectSuggestion = (suggestion: SearchItem) => {
    dispatch(setHighlightedTasks(suggestion.ids));
    dispatch(addFilter(suggestion));
    dispatch(addItemToSearchHistory(suggestion));
    setInput("");
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) =>
        index < suggestions.length - 1 ? index + 1 : 0,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        index > 0 ? index - 1 : suggestions.length - 1,
      );
    } else if (event.key === "Enter") {
      if (activeIndex >= 0) {
        event.preventDefault();
        event.stopPropagation();
        selectSuggestion(suggestions[activeIndex]);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      const highlighted = history.flatMap((item) => item.ids);
      dispatch(setHighlightedTasks(highlighted));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      // document.removeEventListener("mousedown", handleClickOutside); - doublecheck
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleClearAll = () => {
    setInput("");
    dispatch(clearFilters());
    dispatch(clearHistory());
    dispatch(setHighlightedTasks([]));
  };

  const chips = appliedFilters.map((searchItem, index) => (
    <SearchItemChip searchResult={searchItem} key={index} />
  ));

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      searchRef.current &&
      !searchRef.current.contains(event.relatedTarget as Node)
    ) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" style={{ maxWidth: 640, width: "100%" }}>
      <div ref={searchRef} onBlur={handleBlur}>
        {/* search bar */}
        <SearchBar
          input={input}
          onInputChange={setInput}
          appliedFilters={chips}
          onClearAll={handleClearAll}
          placeholder={labels.searchPrompt}
          allowFurtherSearch
          inputRef={inputRef}
          onInputKeyDown={handleInputKeyDown}
        />
        {/* search dropdown */}
        {isOpen && suggestions.length > 0 && (
          <ul
            ref={dropdownRef}
            role="listbox"
            aria-label={labels.results}
            aria-activedescendant={
              activeIndex >= 0 ? `search-suggestion-${activeIndex}` : undefined
            }
            className={searchStyles.searchSugeestionsWrapper}
            // Options aren't focusable, so letting mousedown move focus would blur the input and
            // close the list before the click lands.
            onMouseDown={(event) => event.preventDefault()}
          >
            <div className="flex w-[100%]">
              <p className="[font:var(--font-label)] text-[var(--col-text-secondary)]">
                {labels.results}
              </p>
            </div>
            {suggestions.map((suggestion, index) => (
              <SearchResult
                key={index}
                id={`search-suggestion-${index}`}
                result={suggestion}
                isActive={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectSuggestion(suggestion)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default GanttChartSearchBar;
